import { useCallback, useMemo, useState } from 'react';
import type {
  BatchUpdateTransactionPatch,
  CreateTransactionData,
  ParsedTransactionResponse,
  TransactionType,
} from '@/lib/api';
import { classifyRow, type ReconciliationCase } from './classify';

export type RowIntent = 'create' | 'link' | 'skip' | 'pending';

export interface RowDecision {
  case: ReconciliationCase;
  intent: RowIntent;
  categoryId: string | null;
  manualId: string | null;
  descriptionOverride: string | null;
}

export interface RowPair {
  row: ParsedTransactionResponse;
  fitid: string;
  case: ReconciliationCase;
}

export interface ReconciliationCounts {
  /** OFX rows that are classified as new entries to create. */
  newClassified: number;
  /** OFX rows the backend auto-matched to a single manual entry. */
  autoLink: number;
  /** OFX rows with multiple manual matches (need user pick). */
  ambiguous: number;
  /** OFX rows already imported (FITID present in DB). */
  duplicate: number;
  /** Decisions resolved as "create" (new + ambiguous→createNew + unlinked auto-link). */
  toCreate: number;
  /** Decisions resolved as "link" (auto-link + ambiguous→pickedManual). */
  toLink: number;
  /** Ambiguous rows still awaiting a decision. */
  pending: number;
  /** Re-imports (skipped). */
  skipped: number;
}

export interface SubmissionPayload {
  creates: CreateTransactionData[];
  links: BatchUpdateTransactionPatch[];
  skipped: number;
}

export interface UseOFXReconciliationResult {
  file: File | null;
  pairs: RowPair[];
  decisions: Record<string, RowDecision>;
  duplicatesOpen: boolean;
  rowErrors: Record<string, string>;
  counts: ReconciliationCounts;
  /** Number of "create" decisions that still need a category. */
  missingCategoryCount: number;
  canSubmit: boolean;
  submissionPayload: SubmissionPayload;
  initializeFromPreview: (file: File, parsed: ParsedTransactionResponse[]) => void;
  reset: () => void;
  setCategoryId: (fitid: string, categoryId: string) => void;
  setPickedManualId: (fitid: string, manualId: string) => void;
  setAmbiguousCreateNew: (fitid: string) => void;
  /**
   * Move a "link" decision out of the right column.
   * - case 'auto-link' → flip to 'create' override (lands in left column, needs category)
   * - case 'ambiguous' → reset to 'pending' (lands back in the top decision panel)
   */
  unlinkDecision: (fitid: string) => void;
  /**
   * Revert a "create" override back to its starting state.
   * - case 'auto-link' (overridden) → flip back to 'link' (lands in right column)
   * - case 'ambiguous' (createNew) → reset to 'pending' (lands back in the top panel)
   */
  revertCreateOverride: (fitid: string) => void;
  setDescriptionOverride: (fitid: string, value: string | null) => void;
  toggleDuplicates: () => void;
  setRowError: (fitid: string, message: string) => void;
  clearRowErrors: () => void;
}

function initialDecisionFor(row: ParsedTransactionResponse, c: ReconciliationCase): RowDecision {
  switch (c) {
    case 'new':
      return {
        case: c,
        intent: 'create',
        categoryId: row.category_id ?? null,
        manualId: null,
        descriptionOverride: null,
      };
    case 'auto-link':
      return {
        case: c,
        intent: 'link',
        categoryId: row.category_id ?? null,
        manualId: row.matching_transactions?.[0]?.id ?? null,
        descriptionOverride: null,
      };
    case 'ambiguous':
      return {
        case: c,
        intent: 'pending',
        categoryId: row.category_id ?? null,
        manualId: null,
        descriptionOverride: null,
      };
    case 'duplicate':
      return {
        case: c,
        intent: 'skip',
        categoryId: null,
        manualId: null,
        descriptionOverride: null,
      };
  }
}

function buildInitialState(rows: ParsedTransactionResponse[]) {
  const decisions: Record<string, RowDecision> = {};
  const pairs: RowPair[] = [];

  rows.forEach((row) => {
    const fitid = row.fitid;
    if (!fitid) return;
    const c = classifyRow(row);
    pairs.push({ row, fitid, case: c });
    decisions[fitid] = initialDecisionFor(row, c);
  });

  return { decisions, pairs };
}

export function useOFXReconciliation(): UseOFXReconciliationResult {
  const [file, setFile] = useState<File | null>(null);
  const [pairs, setPairs] = useState<RowPair[]>([]);
  const [decisions, setDecisions] = useState<Record<string, RowDecision>>({});
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const initializeFromPreview = useCallback(
    (newFile: File, parsed: ParsedTransactionResponse[]) => {
      const built = buildInitialState(parsed);
      setFile(newFile);
      setPairs(built.pairs);
      setDecisions(built.decisions);
      setDuplicatesOpen(false);
      setRowErrors({});
    },
    [],
  );

  const reset = useCallback(() => {
    setFile(null);
    setPairs([]);
    setDecisions({});
    setDuplicatesOpen(false);
    setRowErrors({});
  }, []);

  const clearRowError = useCallback((fitid: string) => {
    setRowErrors((prev) => {
      if (!(fitid in prev)) return prev;
      const next = { ...prev };
      delete next[fitid];
      return next;
    });
  }, []);

  const setCategoryId = useCallback(
    (fitid: string, categoryId: string) => {
      setDecisions((prev) => {
        const d = prev[fitid];
        if (!d || d.intent !== 'create') return prev;
        return { ...prev, [fitid]: { ...d, categoryId } };
      });
      clearRowError(fitid);
    },
    [clearRowError],
  );

  const setPickedManualId = useCallback(
    (fitid: string, manualId: string) => {
      setDecisions((prev) => {
        const d = prev[fitid];
        if (!d || d.case !== 'ambiguous') return prev;
        return { ...prev, [fitid]: { ...d, intent: 'link', manualId, categoryId: null } };
      });
      clearRowError(fitid);
    },
    [clearRowError],
  );

  const setAmbiguousCreateNew = useCallback(
    (fitid: string) => {
      setDecisions((prev) => {
        const d = prev[fitid];
        if (!d || d.case !== 'ambiguous') return prev;
        return { ...prev, [fitid]: { ...d, intent: 'create', manualId: null } };
      });
      clearRowError(fitid);
    },
    [clearRowError],
  );

  const unlinkDecision = useCallback(
    (fitid: string) => {
      setDecisions((prev) => {
        const d = prev[fitid];
        if (!d || d.intent !== 'link') return prev;
        if (d.case === 'auto-link') {
          return {
            ...prev,
            [fitid]: { ...d, intent: 'create', categoryId: null, descriptionOverride: null },
          };
        }
        if (d.case === 'ambiguous') {
          return {
            ...prev,
            [fitid]: {
              ...d,
              intent: 'pending',
              manualId: null,
              categoryId: null,
              descriptionOverride: null,
            },
          };
        }
        return prev;
      });
      clearRowError(fitid);
    },
    [clearRowError],
  );

  const revertCreateOverride = useCallback(
    (fitid: string) => {
      setDecisions((prev) => {
        const d = prev[fitid];
        if (!d || d.intent !== 'create') return prev;
        if (d.case === 'auto-link') {
          return {
            ...prev,
            [fitid]: { ...d, intent: 'link', categoryId: null, descriptionOverride: null },
          };
        }
        if (d.case === 'ambiguous') {
          return {
            ...prev,
            [fitid]: {
              ...d,
              intent: 'pending',
              manualId: null,
              categoryId: null,
              descriptionOverride: null,
            },
          };
        }
        return prev;
      });
      clearRowError(fitid);
    },
    [clearRowError],
  );

  const setDescriptionOverride = useCallback(
    (fitid: string, value: string | null) => {
      setDecisions((prev) => {
        const d = prev[fitid];
        if (!d || d.intent !== 'create') return prev;
        if (d.descriptionOverride === value) return prev;
        return { ...prev, [fitid]: { ...d, descriptionOverride: value } };
      });
      clearRowError(fitid);
    },
    [clearRowError],
  );

  const toggleDuplicates = useCallback(() => {
    setDuplicatesOpen((prev) => !prev);
  }, []);

  const setRowError = useCallback((fitid: string, message: string) => {
    setRowErrors((prev) => ({ ...prev, [fitid]: message }));
  }, []);

  const clearRowErrors = useCallback(() => {
    setRowErrors({});
  }, []);

  const counts = useMemo<ReconciliationCounts>(() => {
    let newClassified = 0;
    let autoLink = 0;
    let ambiguous = 0;
    let duplicate = 0;
    let toCreate = 0;
    let toLink = 0;
    let pending = 0;
    let skipped = 0;

    pairs.forEach((p) => {
      const d = decisions[p.fitid];
      if (!d) return;
      if (p.case === 'new') newClassified++;
      else if (p.case === 'auto-link') autoLink++;
      else if (p.case === 'ambiguous') ambiguous++;
      else duplicate++;

      if (d.intent === 'create') toCreate++;
      else if (d.intent === 'link') toLink++;
      else if (d.intent === 'pending') pending++;
      else if (d.intent === 'skip') skipped++;
    });

    return {
      newClassified,
      autoLink,
      ambiguous,
      duplicate,
      toCreate,
      toLink,
      pending,
      skipped,
    };
  }, [pairs, decisions]);

  const missingCategoryCount = useMemo(() => {
    let n = 0;
    pairs.forEach((p) => {
      const d = decisions[p.fitid];
      if (d?.intent === 'create' && !d.categoryId) n++;
    });
    return n;
  }, [pairs, decisions]);

  const canSubmit = useMemo(
    () =>
      pairs.length > 0 &&
      counts.pending === 0 &&
      missingCategoryCount === 0 &&
      counts.toCreate + counts.toLink > 0,
    [pairs.length, counts.pending, counts.toCreate, counts.toLink, missingCategoryCount],
  );

  const submissionPayload = useMemo<SubmissionPayload>(() => {
    const creates: CreateTransactionData[] = [];
    const links: BatchUpdateTransactionPatch[] = [];
    pairs.forEach((p) => {
      const d = decisions[p.fitid];
      if (!d) return;
      if (d.intent === 'create' && d.categoryId) {
        creates.push({
          amount: parseFloat(p.row.amount),
          category_id: d.categoryId,
          date: p.row.date,
          description: d.descriptionOverride ?? p.row.description,
          type: p.row.type as TransactionType,
          external_id: p.fitid,
        });
      } else if (d.intent === 'link' && d.manualId) {
        links.push({ id: d.manualId, external_id: p.fitid });
      }
    });
    return { creates, links, skipped: counts.skipped };
  }, [pairs, decisions, counts.skipped]);

  return {
    file,
    pairs,
    decisions,
    duplicatesOpen,
    rowErrors,
    counts,
    missingCategoryCount,
    canSubmit,
    submissionPayload,
    initializeFromPreview,
    reset,
    setCategoryId,
    setPickedManualId,
    setAmbiguousCreateNew,
    unlinkDecision,
    revertCreateOverride,
    setDescriptionOverride,
    toggleDuplicates,
    setRowError,
    clearRowErrors,
  };
}
